// ─────────────────────────────────────────────────────────────────────────────────────────────
// C-b (decision 77): what a chat used, what it may use, and what its action cards did.
//
// The server decides which AREA each tool belongs to ("Aidey Backend/lib/chatAreas.js") and sends
// the areas a turn used with the reply; the chat stores them on the message as `areas`. Nothing
// here maps tool names, so the tag, the toggles and the server cannot disagree about a tool.
//
//   tags      the history row's tag, from the areas the chat's messages recorded (no tool, no tag)
//   toggles   Tasks / Calendar / Money for THIS chat, stored inside the chat's own messages as a
//             hidden `chat_settings` message (no new column), so they travel with the chat to the
//             server and to another device. The last one wins.
//   cards     action cards under an answer are marked done only by a confirmed call that matches
// ─────────────────────────────────────────────────────────────────────────────────────────────

// Tag colours are tokens (pastel-*), the same family as the starters the mockup tagged.
export const AREA_TAG = {
  money:    { label: 'Money', color: 'pastel-pink' },
  calendar: { label: 'Calendar', color: 'pastel-mint' },
  tasks:    { label: 'Tasks', color: 'pastel-orange' },
  email:    { label: 'Email', color: 'pastel-blue' },
  contacts: { label: 'Contacts', color: 'pastel-lilac' },
  team:     { label: 'Team', color: 'pastel-periwinkle' },
  projects: { label: 'Projects', color: 'pastel-yellow' },
  notes:    { label: 'Notes', color: 'pastel-neutral' },
  home:     { label: 'Home', color: 'pastel-green' },
};

export const TOGGLE_AREAS = ['tasks', 'calendar', 'money'];
export const DEFAULT_TOGGLES = { tasks: true, calendar: true, money: true };
export const SETTINGS_ROLE = 'chat_settings';

// The areas the chat's own messages recorded, most used first (ties: first used first). Older
// cards that predate `areas` still say what they were: an email draft, a workload or absence card.
export function areasFromMessages(messages) {
  const count = new Map();
  const bump = (a) => { if (AREA_TAG[a]) count.set(a, (count.get(a) || 0) + 1); };
  for (const m of Array.isArray(messages) ? messages : []) {
    if (!m || typeof m !== 'object') continue;
    if (Array.isArray(m.areas)) { m.areas.forEach(bump); continue; }
    if (m.role === 'email_draft') bump('email');
    else if (m.role === 'workload_redistribution') bump('tasks');
    else if (m.role === 'absence_delegation') bump('team');
  }
  return [...count.entries()].sort((a, b) => b[1] - a[1]).map(([a]) => a);
}

// One tag per row: this browser's copy of the messages first, then what the server read from its
// copy (a chat that exists only on the server). Null when the chat used nothing.
export function chatTag(session) {
  if (!session) return null;
  const local = areasFromMessages(session.messages);
  const server = Array.isArray(session.areas) ? session.areas.filter((a) => AREA_TAG[a]) : [];
  const area = local[0] || server[0];
  return area ? { area, ...AREA_TAG[area] } : null;
}

// ── Toggles ─────────────────────────────────────────────────────────────────
export function togglesFromMessages(messages) {
  const list = Array.isArray(messages) ? messages : [];
  for (let i = list.length - 1; i >= 0; i -= 1) {
    const m = list[i];
    if (m && m.role === SETTINGS_ROLE && m.areas && typeof m.areas === 'object') {
      const out = { ...DEFAULT_TOGGLES };
      for (const a of TOGGLE_AREAS) if (m.areas[a] === false) out[a] = false;
      return out;
    }
  }
  return { ...DEFAULT_TOGGLES };
}

// A hidden message (synthetic: never shown, never replayed to the model, which only reads user
// and assistant messages).
export function settingsMessage(toggles, now = Date.now()) {
  const areas = {};
  for (const a of TOGGLE_AREAS) areas[a] = toggles[a] !== false;
  return { role: SETTINGS_ROLE, areas, synthetic: true, ts: now };
}

// Keep the last `max` messages, and the chat's latest settings even when it is older than that,
// so a long chat does not quietly switch its toggles back on.
export function trimKeepingSettings(messages, max) {
  const list = Array.isArray(messages) ? messages : [];
  if (list.length <= max) return list;
  const tail = list.slice(-max);
  if (tail.some((m) => m && m.role === SETTINGS_ROLE)) return tail;
  let last = null;
  for (let i = list.length - max - 1; i >= 0; i -= 1) {
    if (list[i] && list[i].role === SETTINGS_ROLE) { last = list[i]; break; }
  }
  return last ? [last, ...tail.slice(1)] : tail;
}

// ── Action cards ────────────────────────────────────────────────────────────
// A card is done when a call that does ITS action came back ok: the same tool and the same record.
export function cardMatches(card, tool, ref) {
  const m = card && card.match;
  if (!m || !Array.isArray(m.tools) || !m.tools.includes(tool)) return false;
  return ref != null && String(ref) === String(m.value);
}

// After a confirmed call succeeded: mark every card it completes. Returns the same array when
// nothing changed.
export function markCardsDone(messages, tool, input) {
  let changed = false;
  const next = messages.map((m) => {
    if (!m || !Array.isArray(m.actionCards)) return m;
    let hit = false;
    const cards = m.actionCards.map((c) => {
      const key = c && c.match && c.match.key;
      if (c.done || !key || !cardMatches(c, tool, input && input[key])) return c;
      hit = true;
      return { ...c, done: true };
    });
    if (!hit) return m;
    changed = true;
    return { ...m, actionCards: cards };
  });
  return changed ? next : messages;
}

// From a chat turn's record ([{ name, ok, ref }]): only the calls that worked.
export function markCardsDoneFromOutcomes(messages, outcomes) {
  let out = messages;
  for (const o of Array.isArray(outcomes) ? outcomes : []) {
    if (!o || o.ok !== true || o.ref == null) continue;
    out = out.map((m) => {
      if (!m || !Array.isArray(m.actionCards)) return m;
      let hit = false;
      const cards = m.actionCards.map((c) => (c.done || !cardMatches(c, o.name, o.ref) ? c : ((hit = true), { ...c, done: true })));
      return hit ? { ...m, actionCards: cards } : m;
    });
  }
  return out;
}
